import {AsyncButton} from "@client/components/Button";
import {secondaryButtonClass} from "@client/components/styles";
import * as dates from "@client/dates";
import * as dialogs from "@client/features/dialogs";
import * as habits from "@client/features/habits";
import {TitleAndDescription} from "@client/features/habits";
import * as healthkit from "@client/features/healthkit";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";
import * as forms from "@client/forms";
import * as hardware from "@client/hardware";
import * as router from "@client/router";
import {useAppDispatch, useAppSelector} from "@client/store";
import * as utils from "@client/utils";
import {MinusIcon, PlusIcon} from "@heroicons/react/16/solid";
import NumberFlow from "@number-flow/react";
import {classNames, server} from "@reactivated";
import {produce} from "immer";
import * as React from "react";

export function WeighIn({
    profile,
    journal,
}: {
    profile: server.core.rpc.Profile.output;
    journal: server.journal.day.Journal.output;
    setJournal: (journal: server.journal.day.Journal.output) => void;
}) {
    const dispatch = useAppDispatch();
    const initial = [journal.day.measurements[0], null][0];
    const currentWeight = initial?.weight ?? null;
    const isImperial = forms.useIsImperial();

    // const isComplete = Object.values(initial ?? {}).some((value) => value != null);
    const isComplete = initial?.weight != null;

    const title = "Weight";
    const description =
        currentWeight == null ? (
            <div className="flex h-7 items-center text-sm/6 font-light text-gray-950">
                Log weight
            </div>
        ) : (
            <div className="flex h-7 items-center text-lg font-light text-gray-950">
                {utils.lbOrKg(currentWeight, isImperial)}
            </div>
        );

    const content = <TitleAndDescription title={title} description={description} />;
    return (
        <habits.JournalHabitShell
            label="Weigh In"
            habit={habits.habits.WEIGHT}
            content={content}
            isComplete={isComplete}
            onClick={() => {
                void hardware.haptic();
                if (profile.user.get_measurements.length === 0) {
                    dispatch(
                        notify("Your manager has turned off this feature", {
                            type: "error",
                        }),
                    );
                    return;
                }
                void router.push(
                    "update_measurements.index",
                    {
                        key: journal.day.key,
                    },
                    {journal},
                );
            }}
        />
    );
}

export function Steps({
    profile,
    journal,
    setJournal,
    forceLinkedToAppleHealth,
}: {
    profile: server.core.rpc.Profile.output;
    journal: server.journal.day.Journal.output;
    setJournal: (journal: server.journal.day.Journal.output) => void;
    forceLinkedToAppleHealth?: boolean;
}) {
    const {deviceUUID, platform} = React.use(hardware.getAppInfo());
    const data = useAppSelector((state) => orm.getMetrics(state, journal));
    const {assignment, day} = journal;

    const enableAppleHealth = dialogs.useDialog({
        Content: ({close}) => {
            return <healthkit.AppleHealthDialog close={close} />;
        },
    });
    // data.STEPS = 45000;

    React.useEffect(() => {
        const listener = hardware.App.addListener("appStateChange", (appState) => {
            const status = appState.isActive ? "active" : "inactive";

            void healthkit.checkAndSyncHealthData({
                profile: profile,
                trace: `app-state-${status}`,
            });
        });

        return () => {
            void (async () => {
                void (await listener).remove();
            })();
        };
    }, []);

    const settings = profile.settings.by_device?.[deviceUUID]?.apple_health;
    // SHOULD_REQUEST is a linked, syncing state — it only adds "the UI may offer
    // an update". Whether to actually offer it comes from live iOS state, never
    // from the persisted setting.
    const isLinkedToAppleHealth =
        settings?.status == "ENABLED" || settings?.status == "SHOULD_REQUEST";
    const healthNeedsUpdate = useAppSelector(orm.getHealthNeedsUpdate);

    const options = {
        isComplete:
            assignment?.habits.STEPS == null
                ? null
                : (data.STEPS ?? 0) >= assignment.habits.STEPS,
        handler: {
            decrement: () => {
                hardware.haptic();
                const updatedMetric = {
                    value: Math.max((data.STEPS ?? 0) - 500, 0),
                    start_date: day.key,
                    end_date: day.key,
                    timezone: dates.deviceTimezone(),
                    is_manual: true,
                    apple_health_uuids: [],
                    human: "",
                };

                setJournal(
                    produce(journal, (draft) => {
                        draft.metrics.STEPS = updatedMetric;
                    }),
                );

                void server.journal.day.update_metric_aggregates({
                    user_id: journal.day.user_id,
                    metrics: {
                        [day.key]: {
                            STEPS: updatedMetric,
                        },
                    },
                    workouts: {},
                });
            },
            increment: () => {
                hardware.haptic();
                const updatedMetric = {
                    value: Math.max((data.STEPS ?? 0) + 500, 0),
                    start_date: day.key,
                    end_date: day.key,
                    timezone: dates.deviceTimezone(),
                    is_manual: true,
                    apple_health_uuids: [],
                    human: "",
                };

                setJournal(
                    produce(journal, (draft) => {
                        draft.metrics.STEPS = updatedMetric;
                    }),
                );

                void server.journal.day.update_metric_aggregates({
                    user_id: journal.day.user_id,
                    metrics: {
                        [day.key]: {
                            STEPS: updatedMetric,
                        },
                    },
                    workouts: {},
                });
            },
        },
    };
    const stepper = (
        <div className="mt-0.5 -ml-0.5 flex divide-x rounded-full">
            <button
                onClick={options.handler.decrement}
                type="button"
                className={classNames(
                    secondaryButtonClass,
                    "rounded-l-full! rounded-r-none! px-1!",
                )}
            >
                <MinusIcon className="size-3" aria-hidden="true" />
            </button>
            <button
                onClick={options.handler.increment}
                type="button"
                className={classNames(
                    secondaryButtonClass,
                    "rounded-r-full! rounded-l-none! px-1!",
                )}
            >
                <PlusIcon className="size-3" aria-hidden="true" />
            </button>
        </div>
    );

    const stepperOrSetup =
        forceLinkedToAppleHealth != true &&
        (settings?.status == "NEVER" || platform != "ios") ? (
            <div className="absolute -top-0.5 -right-0.5">{stepper}</div>
        ) : platform == "ios" && isLinkedToAppleHealth && healthNeedsUpdate ? (
            <span className="absolute -top-1 right-0 -ml-4">
                <AsyncButton
                    className={secondaryButtonClass}
                    onClick={() => {
                        void hardware.haptic();
                        return enableAppleHealth();
                    }}
                >
                    Update
                </AsyncButton>
            </span>
        ) : (platform != "ios" && forceLinkedToAppleHealth == true) ||
          isLinkedToAppleHealth ? (
            <div className="absolute top-0.5 left-11">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-4"
                >
                    <path d="M12.232 4.232a2.5 2.5 0 0 1 3.536 3.536l-1.225 1.224a.75.75 0 0 0 1.061 1.06l1.224-1.224a4 4 0 0 0-5.656-5.656l-3 3a4 4 0 0 0 .225 5.865.75.75 0 0 0 .977-1.138 2.5 2.5 0 0 1-.142-3.667l3-3Z" />
                    <path d="M11.603 7.963a.75.75 0 0 0-.977 1.138 2.5 2.5 0 0 1 .142 3.667l-3 3a2.5 2.5 0 0 1-3.536-3.536l1.225-1.224a.75.75 0 0 0-1.061-1.06l-1.224 1.224a4 4 0 1 0 5.656 5.656l3-3a4 4 0 0 0-.225-5.865Z" />
                </svg>
            </div>
        ) : (
            <span className="absolute -top-1 right-0 -ml-4">
                <AsyncButton
                    className={secondaryButtonClass}
                    onClick={() => {
                        void hardware.haptic();
                        return enableAppleHealth();
                    }}
                >
                    Enable
                </AsyncButton>
            </span>
        );

    const suffix =
        assignment?.habits.STEPS != null
            ? ` / ${assignment.habits.STEPS / 1000}k`
            : null;

    const description = (
        <div className="flex items-baseline gap-1 text-sm font-light whitespace-nowrap text-gray-950">
            <NumberFlow
                value={Math.round(data.STEPS ?? 0)}
                className="missing-tailwind-support-steps text-lg text-gray-950"
                suffix={suffix ?? undefined}
            />
        </div>
    );

    const content = (
        <div className="relative flex flex-1 items-center gap-1">
            {enableAppleHealth.dialog}
            <TitleAndDescription title="Steps" description={description}>
                {stepperOrSetup}
            </TitleAndDescription>
        </div>
    );

    return (
        <habits.JournalHabitShell
            label="Steps"
            habit={habits.habits.STEPS}
            content={content}
            isComplete={
                platform == "ios" && !isLinkedToAppleHealth ? null : options.isComplete
            }
        />
    );
}
